import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Package, Factory, ShoppingCart, Truck, TrendingUp, AlertCircle } from 'lucide-react';
import apiClient from '@/services/apiClient';

const StatCard = ({ label, value, sub, accent = 'bg-slate-50' }) => (
  <div className={`${accent} rounded-lg p-3 text-center`}>
    <p className="text-xs text-gray-500 font-medium">{label}</p>
    <p className="text-2xl font-bold text-gray-800">{value ?? '—'}</p>
    {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
  </div>
);

const Panel = ({ title, icon: Icon, children }) => (
  <div className="border rounded-lg p-3 bg-white">
    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5 mb-2">
      <Icon className="w-4 h-4 text-indigo-600" />{title}
    </h3>
    {children}
  </div>
);

const StockViewModal = ({ open, onClose, poItemId }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || !poItemId) return;
    setLoading(true); setErr(null); setData(null);
    apiClient.get(`/purchase-orders/items/${poItemId}/stock-view`)
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.detail || 'Failed to load stock view'))
      .finally(() => setLoading(false));
  }, [open, poItemId]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="stock-view-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-indigo-600" />
            Stock View {data?.design_name ? `· ${data.design_name}` : ''}
          </DialogTitle>
        </DialogHeader>

        {loading && <div className="text-center py-8 text-gray-400">Loading stock data...</div>}
        {err && <div className="text-center py-8 text-red-500 flex items-center justify-center gap-2"><AlertCircle className="w-4 h-4" />{err}</div>}
        {data?.warning && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>{data.warning}</div>
          </div>
        )}

        {data && !data.warning && (
          <div className="space-y-4">
            {/* Panel 1: FG Stock */}
            <Panel title="FG Stock" icon={Package}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <StatCard label="SFG Storage" value={data.fg_stock.sfg_storage} accent="bg-blue-50" />
                <StatCard label="In WIP (SFG)" value={data.fg_stock.in_wip} accent="bg-amber-50" />
                <StatCard label="FG Storage" value={data.fg_stock.fg_storage} accent="bg-green-50" />
                <StatCard label="Total FG" value={data.fg_stock.total} accent="bg-indigo-50" />
              </div>
            </Panel>

            {/* Panel 2: WIP by Section */}
            <Panel title="WIP Stock by Section" icon={Factory}>
              {Object.keys(data.wip_by_section).length === 0 ? (
                <p className="text-xs text-gray-400 italic">No open WIP in any tracked section.</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {Object.entries(data.wip_by_section).map(([sec, qty]) => (
                    <StatCard key={sec} label={sec} value={qty} accent="bg-slate-50" />
                  ))}
                </div>
              )}
            </Panel>

            {/* Panel 3: Pending Orders (POs + SOs side by side) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Panel title={`Pending Purchase Orders (${data.pending_purchase_orders.count})`} icon={ShoppingCart}>
                <div className="text-sm mb-2">
                  Total balance: <span className="font-semibold">{data.pending_purchase_orders.balance_qty}</span>
                </div>
                {data.pending_purchase_orders.rows.length > 0 ? (
                  <div className="max-h-40 overflow-y-auto">
                    <Table>
                      <TableHeader><TableRow><TableHead>PO#</TableHead><TableHead>Vendor</TableHead><TableHead className="text-right">Balance</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {data.pending_purchase_orders.rows.map(r => (
                          <TableRow key={r.po_id}>
                            <TableCell className="font-mono text-xs">{r.po_number}</TableCell>
                            <TableCell className="text-xs">{r.vendor_name}</TableCell>
                            <TableCell className="text-right">{r.balance_qty}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : <p className="text-xs text-gray-400 italic">None</p>}
              </Panel>

              <Panel title={`Pending Sales Orders (${data.pending_sales_orders.count})`} icon={ShoppingCart}>
                <div className="text-sm mb-2">
                  Total pending qty: <span className="font-semibold">{data.pending_sales_orders.pending_qty}</span>
                </div>
                {data.pending_sales_orders.rows.length > 0 ? (
                  <div className="max-h-40 overflow-y-auto">
                    <Table>
                      <TableHeader><TableRow><TableHead>Order#</TableHead><TableHead>Customer</TableHead><TableHead className="text-right">Qty</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {data.pending_sales_orders.rows.map((r, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-mono text-xs">{r.order_number}</TableCell>
                            <TableCell className="text-xs">{r.customer_code}</TableCell>
                            <TableCell className="text-right">{r.qty}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : <p className="text-xs text-gray-400 italic">None</p>}
              </Panel>
            </div>

            {/* Panel 4: DC Qty */}
            <Panel title="DC Qty (Dispatch Challan)" icon={Truck}>
              <div className="grid grid-cols-2 gap-2">
                <StatCard label={`Current FY (${data.dc_qty.current_fy_label})`} value={data.dc_qty.current_fy} accent="bg-blue-50" />
                <StatCard label={`Last FY (${data.dc_qty.last_fy_label})`} value={data.dc_qty.last_fy} accent="bg-slate-50" />
              </div>
            </Panel>

            {/* Panel 5: Inventory Turnover */}
            <Panel title="Inventory Turnover" icon={TrendingUp}>
              <div className="grid grid-cols-2 gap-2">
                <StatCard
                  label="Stock Turn / year"
                  value={data.turnover.stock_turn_per_year != null ? `${data.turnover.stock_turn_per_year}×` : '—'}
                  accent="bg-green-50"
                />
                <StatCard
                  label="Days of Inventory"
                  value={data.turnover.days_of_inventory != null ? `${data.turnover.days_of_inventory} d` : '—'}
                  accent="bg-amber-50"
                />
              </div>
              <p className="text-xs text-gray-500 italic mt-2">{data.turnover.note}</p>
            </Panel>

            {data.resolved_barcodes?.length > 0 && (
              <div className="text-xs text-gray-400">
                Matched SKUs:{' '}
                {data.resolved_barcodes.map(b => <Badge key={b} variant="outline" className="mr-1 text-xs">{b}</Badge>)}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StockViewModal;
